// Комментарий на русском: Stack Engine — логика группировки сообщений в стэки (runs).
// Реализует правила из newChatScroll.md: N-1 в стэке, N-ое отдельно (активный ран).

export type StackMode = 'collapsed' | 'expanded' | 'merged';

export interface StackState {
  mode: StackMode;
  carouselIndex?: number; // Какое сообщение сейчас показано наверху стэка
  isActiveRun: boolean; // Активный ран (последний, собеседник ещё не ответил)
  owner: 'me' | 'other';
}

export interface RenderItem {
  kind: 'message' | 'stack';
  // Для kind === 'message':
  messageId?: number;
  // Для kind === 'stack':
  stackId?: string;
  messageIds?: number[];
  state?: StackState;
}

export interface MessageForStacking {
  id: number;
  from_user: { id: number };
  created_at: string;
  [key: string]: any;
}

export interface StackEngineOptions {
  meId: number;
  // Таймаут между сообщениями (в миллисекундах), после которого начинается новый ран
  gapTimeoutMs?: number;
  // Читалки (receipts) для определения закрытия рана
  receipts?: Map<number, boolean>; // messageId -> is_read
}

interface Run {
  messages: MessageForStacking[];
  senderId: number;
  startTime: number;
  endTime: number;
  isClosed: boolean; // Закрыт ли ран (другой пользователь ответил или таймаут)
}

// Комментарий на русском: основная функция — преобразует плоский массив сообщений в RenderItems.
export function buildStackRenderItems(
  messages: MessageForStacking[],
  options: StackEngineOptions
): RenderItem[] {
  const { meId, gapTimeoutMs = 5 * 60 * 1000, receipts } = options;
  
  if (!messages.length) return [];

  // Комментарий на русском: группируем сообщения в раны (последовательные от одного отправителя).
  const runs: Run[] = [];
  let currentRun: Run | null = null;

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const senderId = msg.from_user?.id || 0;
    const msgTime = new Date(msg.created_at).getTime();

    if (!currentRun || currentRun.senderId !== senderId) {
      // Комментарий на русском: начинаем новый ран (сменился отправитель).
      if (currentRun) {
        currentRun.isClosed = true; // Предыдущий ран закрывается
        runs.push(currentRun);
      }
      currentRun = {
        messages: [msg],
        senderId,
        startTime: msgTime,
        endTime: msgTime,
        isClosed: false,
      };
    } else {
      // Комментарий на русском: проверяем таймаут между сообщениями.
      const gap = msgTime - currentRun.endTime;
      if (gap > gapTimeoutMs) {
        // Комментарий на русском: таймаут превышен — закрываем текущий ран и начинаем новый.
        currentRun.isClosed = true;
        runs.push(currentRun);
        currentRun = {
          messages: [msg],
          senderId,
          startTime: msgTime,
          endTime: msgTime,
          isClosed: false,
        };
      } else {
        // Комментарий на русском: добавляем сообщение в текущий ран.
        currentRun.messages.push(msg);
        currentRun.endTime = msgTime;
      }
    }
  }

  // Комментарий на русском: последний ран — активный (если не закрыт другими правилами).
  if (currentRun) {
    runs.push(currentRun);
  }

  // Комментарий на русском: преобразуем раны в RenderItems.
  const renderItems: RenderItem[] = [];

  for (let i = 0; i < runs.length; i++) {
    const run = runs[i];
    const isLastRun = i === runs.length - 1;
    const isActiveRun = isLastRun && !run.isClosed;
    const owner = run.senderId === meId ? 'me' : 'other';

    if (run.messages.length === 1) {
      // Комментарий на русском: один элемент в ране — отображаем как обычное сообщение.
      renderItems.push({
        kind: 'message',
        messageId: run.messages[0].id,
      });
    } else if (run.messages.length === 2) {
      if (isActiveRun) {
        // Комментарий на русском: активный ран — N-1 в стэке, N-ое отдельно (даже для N=2).
        const first = run.messages[0];
        const last = run.messages[1];
        const stackId = `stack-${first.id}-${first.id}`;
        renderItems.push({
          kind: 'stack',
          stackId,
          messageIds: [first.id],
          state: {
            mode: 'collapsed',
            carouselIndex: 0,
            isActiveRun: true,
            owner,
          },
        });
        renderItems.push({
          kind: 'message',
          messageId: last.id,
        });
      } else {
        // Комментарий на русском: закрытый ран — все сообщения в один стэк.
        const stackId = `stack-${run.messages[0].id}-${run.messages[run.messages.length - 1].id}`;
        renderItems.push({
          kind: 'stack',
          stackId,
          messageIds: run.messages.map((m) => m.id),
          state: {
            mode: 'collapsed',
            carouselIndex: 0,
            isActiveRun: false,
            owner,
          },
        });
      }
    } else {
      // Комментарий на русском: run.messages.length >= 3
      if (isActiveRun) {
        // Комментарий на русском: активный ран — N-1 в стэке, N-ое отдельно.
        const stackMessages = run.messages.slice(0, -1);
        const lastMessage = run.messages[run.messages.length - 1];
        const stackId = `stack-${stackMessages[0].id}-${stackMessages[stackMessages.length - 1].id}`;

        renderItems.push({
          kind: 'stack',
          stackId,
          messageIds: stackMessages.map((m) => m.id),
          state: {
            mode: 'collapsed',
            carouselIndex: 0,
            isActiveRun: true,
            owner,
          },
        });
        renderItems.push({
          kind: 'message',
          messageId: lastMessage.id,
        });
      } else {
        // Комментарий на русском: закрытый ран — все сообщения в один стэк.
        const stackId = `stack-${run.messages[0].id}-${run.messages[run.messages.length - 1].id}`;
        renderItems.push({
          kind: 'stack',
          stackId,
          messageIds: run.messages.map((m) => m.id),
          state: {
            mode: 'collapsed',
            carouselIndex: 0,
            isActiveRun: false,
            owner,
          },
        });
      }
    }
  }

  return renderItems;
}

// Комментарий на русском: вспомогательная функция — развернуть RenderItems обратно в плоский массив сообщений (для совместимости).
export function flattenRenderItems(
  renderItems: RenderItem[],
  messagesMap: Map<number, MessageForStacking>
): MessageForStacking[] {
  const result: MessageForStacking[] = [];
  
  for (const item of renderItems) {
    if (item.kind === 'message' && item.messageId) {
      const msg = messagesMap.get(item.messageId);
      if (msg) result.push(msg);
    } else if (item.kind === 'stack' && item.messageIds) {
      for (const msgId of item.messageIds) {
        const msg = messagesMap.get(msgId);
        if (msg) result.push(msg);
      }
    }
  }
  
  return result;
}
